import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	IconButton,
	Collapse,
	Grid,
	Skeleton,
	Card,
	CardContent,
	Avatar,
	Divider,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import StoreIcon from "@mui/icons-material/Store";
import { useEffect, useState, Fragment } from "react";
import axios from "axios";

/* -----------------------------
   Utils
----------------------------- */
const formatToCrore = (amt = 0) => `${(amt / 10000000).toFixed(2)} Cr`;

/* -----------------------------
   Skeleton
----------------------------- */
const TableSkeleton = () => (
	<Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0", width: "100%" }}>
		<CardContent>
			<Box display="flex" alignItems="center" mb={3}>
				<Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
				<Box width="100%">
					<Skeleton width="30%" height={20} />
					<Skeleton width="50%" height={30} />
				</Box>
			</Box>
			<Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 2 }} />
		</CardContent>
	</Card>
);

/* -----------------------------
   Component
----------------------------- */
const StoreMopSummaryTable = ({ startDate, endDate, runKey }) => {
	const [loading, setLoading] = useState(false);
	const [stores, setStores] = useState([]);
	const [openRow, setOpenRow] = useState(null);

	useEffect(() => {
		if (!runKey) return;

		const fetchData = async () => {
			setLoading(true);
			setStores([]);
			setOpenRow(null);

			try {
				const { data } = await axios.post("/api/pos_reco/get-storewise-mop-summary", {
					start_date: startDate,
					end_date: endDate,
				});

				setStores(data?.stores || []);
			} catch (err) {
				console.error("Store MOP summary failed", err);
				setStores([]);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [runKey]);

	/* ================= LOADING ================= */
	if (loading) return <TableSkeleton />;

	/* ================= NO DATA ================= */
	if (!stores || stores.length === 0) {
		return (
			<Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0", width: "100%" }}>
				<CardContent sx={{ display: "flex", justifyContent: "center", p: 4 }}>
					<Typography color="text.secondary">No store-wise MOP data available for the selected date range.</Typography>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0", width: "100%" }}>
			<CardContent>
				<Box display="flex" alignItems="center" gap={2} mb={3}>
					<Avatar sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", width: 48, height: 48 }}>
						<StoreIcon />
					</Avatar>
					<Box>
						<Typography variant="subtitle2" color="text.secondary" fontWeight={600} letterSpacing={0.5}>
							STORE-WISE MOP SPLIT
						</Typography>
						<Typography variant="h5" fontWeight={700} color="text.primary">
							Store Performance
						</Typography>
					</Box>
				</Box>

				<TableContainer sx={{ maxHeight: 500 }}>
					<Table size="small" stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell width="50" sx={{ bgcolor: "#f5f5f5", fontWeight: 600 }}>
									Details
								</TableCell>
								<TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: 600 }}>Store Code</TableCell>
								<TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: 600 }}>Store Name</TableCell>
								<TableCell align="right" sx={{ bgcolor: "#f5f5f5", fontWeight: 600 }}>
									Total Sale
								</TableCell>
							</TableRow>
						</TableHead>

						<TableBody>
							{stores.map((store) => {
								const isOpen = openRow === store.site_code;

								return (
									<Fragment key={store.site_code}>
										<TableRow
											hover
											sx={{ "& > *": { borderBottom: "unset" }, cursor: "pointer" }}
											onClick={() => setOpenRow(isOpen ? null : store.site_code)}>
											<TableCell>
												<IconButton
													aria-label="expand row"
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														setOpenRow(isOpen ? null : store.site_code);
													}}>
													{isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
												</IconButton>
											</TableCell>

											<TableCell>
												<Typography fontWeight={500}>{store.site_code}</Typography>
											</TableCell>
											<TableCell>{store.short_name}</TableCell>

											<TableCell align="right">
												<Typography fontWeight={600}>
													₹ {Number(store.total_amount).toLocaleString("en-IN")}
												</Typography>
											</TableCell>
										</TableRow>

										{/* ===== EXPANDED ROW ===== */}
										<TableRow>
											<TableCell colSpan={4} sx={{ paddingBottom: 0, paddingTop: 0 }}>
												<Collapse in={isOpen} timeout="auto" unmountOnExit>
													<Box sx={{ margin: 2, p: 2, bgcolor: "#f9fafb", borderRadius: 2 }}>
														<Typography
															variant="subtitle2"
															gutterBottom
															fontWeight={600}
															color="text.secondary">
															MOP Breakdown for {store.short_name}
														</Typography>
														<Divider sx={{ mb: 2 }} />
														<Grid container spacing={2}>
															{(store.mop_breakdown || []).map((row) => (
																<Grid item xs={12} sm={6} md={4} key={row.mop}>
																	<Box
																		display="flex"
																		justifyContent="space-between"
																		p={1.5}
																		sx={{
																			bgcolor: "white",
																			borderRadius: 1,
																			border: "1px solid #eee",
																		}}>
																		<Typography variant="body2" color="text.secondary">
																			{row.mop}
																		</Typography>
																		<Typography variant="body2" fontWeight={600}>
																			₹ {Number(row.amount).toLocaleString("en-IN")}
																		</Typography>
																	</Box>
																</Grid>
															))}
														</Grid>
													</Box>
												</Collapse>
											</TableCell>
										</TableRow>
									</Fragment>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</CardContent>
		</Card>
	);
};

export default StoreMopSummaryTable;
